<template>
  <v-card class="mt-4" variant="outlined">
    <v-form ref="form">
      <template v-if="!hideTitle">
        <v-toolbar flat>
          <v-toolbar-title>
            <v-icon class="mr-1">mdi-repeat</v-icon>
            {{ title }}
          </v-toolbar-title>
        </v-toolbar>
        <v-divider />
      </template>
      <v-card-subtitle class="pb-0">Repeat *</v-card-subtitle>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-btn-toggle v-model="formData.frequency" mandatory class="mt-2">
              <v-btn size="small" :value="Frequency.DAILY">
                Daily
              </v-btn>
              <v-btn size="small" :value="Frequency.WEEKLY">
                Weekly
              </v-btn>
              <v-btn size="small" :value="Frequency.MONTHLY">
                Monthly
              </v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="6">
            <CommonDatePicker
              label="Start Date *"
              :enter-date="formData.dateStart"
              clearable
              dense
              outlined
              :min-iso-date="minStartDate"
              :rules="[v => !!v || 'Field is required!']"
              @updateDate="formData.dateStart = $event"
            />
          </v-col>
          <v-col cols="6">
            <CommonDatePicker
              :label="`End Date${endDateRequired ? ' *' : ''}`"
              :enter-date="formData.dateEnd"
              clearable
              dense
              outlined
              :rules="endDateRequired ? [v => !!v || 'Field is required!'] : []"
              :min-iso-date="minEndDate"
              @updateDate="formData.dateEnd = $event"
            />
          </v-col>
          <v-col v-if="[Frequency.WEEKLY, Frequency.MONTHLY].includes(formData.frequency)" cols="12">
            <v-row v-if="formData.frequency === Frequency.WEEKLY">
              <v-col cols="4">
                <IntegerInput
                  v-model="formData.interval"
                  label="Every Week(s) *"
                  density="compact"
                  hide-details="auto"
                  :rules="[v => !!v || 'Field is required!']"
                />
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="formData.dayOfWeek"
                  :items="daysOfWeekItems"
                  item-title="text"
                  label="On *"
                  autocomplete="off"
                />
              </v-col>
            </v-row>
            <v-row v-if="formData.frequency === Frequency.MONTHLY">
              <v-col cols="4">
                <IntegerInput
                  v-model="formData.interval"
                  label="Every Month(s) *"
                  density="compact"
                  hide-details="auto"
                  :rules="[v => !!v || 'Field is required!']"
                />
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="formData.bysetpos"
                  :items="bysetposItems"
                  item-title="text"
                  label="On *"
                  autocomplete="off"
                />
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="formData.dayOfWeek"
                  :items="daysOfWeekItems"
                  item-title="text"
                  autocomplete="off"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12">
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="formData.time"
                  :menu-props="{ }"
                  prepend-inner-icon="mdi-clock-outline"
                  label="Time *"
                  persistent-placeholder
                  :items="timeItems"
                  item-title="text"
                  :rules="[v => !!v || 'Field is required!']"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" data-track="marketing.eblast-schedule.save" @click="submitHandler">
          Save
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script lang="ts">
import { DateTime } from 'luxon';
import { Frequency, RRule, Weekday, Options } from 'rrule';
import { defineComponent } from 'vue';

import { Nullable } from '@/utils/types';
import { ITextValue, VForm } from '@/types/common';
import { getDaysOfWeekItems, getBysetposItems, getTimeItems, parseRRuleString, parseWeekday } from '@/bundles/MarketingTemplate/helpers';
import { formatJSDate, formatTimeItem } from '@/utils/filters';

import CommonDatePicker from '@/components/common-components/pickers/CommonDatePicker.vue';
import IntegerInput from '@/bundles/Common/components/inputs/IntegerInput.vue';

interface IFormData {
  frequency: Frequency,
  dateStart: Nullable<string>;
  dateEnd: Nullable<string>;
  interval: number;
  dayOfWeek: Weekday;
  time: Nullable<string>;
  bysetpos: number;
}

interface IScheduleFormData {
  Frequency: typeof Frequency;
  daysOfWeekItems?: ITextValue<Weekday>[];
  bysetposItems?: ITextValue<number>[];
  formData: IFormData;
}

const getInitialScheduleData = (): IFormData => ({
  frequency: RRule.WEEKLY,
  dateStart: null,
  dateEnd: null,
  interval: 1,
  dayOfWeek: RRule.MO,
  time: null,
  bysetpos: 1
})

const getScheduleDataFromRRule = (rruleEntry: string): IFormData => {
  const options = parseRRuleString(rruleEntry);

  return {
    bysetpos: (Array.isArray(options.bysetpos) ? options.bysetpos[0] : options.bysetpos) || 1,
    dateEnd: formatJSDate(options.until, true) || null,
    dateStart: formatJSDate(options.dtstart, true) || null,
    dayOfWeek: parseWeekday(options.byweekday),
    frequency: options.freq || RRule.WEEKLY,
    interval: options.interval || 1,
    time: `${formatTimeItem(options.byhour)}:${formatTimeItem(options.byminute)}`,
  };
}

export default defineComponent({
  name: 'ScheduleForm',

  components: {
    CommonDatePicker,
    IntegerInput
  },

  props: {
    rruleEntry: {
      type: String,
      default: null
    },
    minStartDate: {
      type: String, // 'YYYY-MM-DD'
      default: null
    },
    title: {
      type: String,
      default: 'Recurring Schedule'
    },
    hideTitle: {
      type: Boolean,
      default: false
    },
    endDateRequired: {
      type: Boolean,
      default: false
    }
  },

  emits: ['submit'],

  data (): IScheduleFormData {
    return {
      Frequency: Frequency,
      daysOfWeekItems: getDaysOfWeekItems(),
      bysetposItems: getBysetposItems(),
      // loaded here rather than in mounted so the timeItems watcher never sees the saved schedule as a change
      formData: this.rruleEntry ? getScheduleDataFromRRule(this.rruleEntry) : getInitialScheduleData(),
    };
  },

  computed: {
    minEndDate (): Nullable<string> {
      return this.formData.dateStart ? DateTime.fromISO(this.formData.dateStart).toISODate() : DateTime.now().toISODate();
    },

    timeItems (): ITextValue<string>[] {
      return getTimeItems(this.formData.dateStart);
    },
  },

  watch: {
    // a time picked before switching the start date to today may no longer be offered
    timeItems (items: ITextValue<string>[]) {
      if (this.formData.time && !items.some(item => item.value === this.formData.time)) {
        this.formData.time = null;
      }
    },
  },

  created () {
    this.Frequency = Frequency;
  },

  methods: {
    async submitHandler (): Promise<boolean> {
      const { valid } = await (this.$refs.form as VForm).validate();
      if (!valid) {
        return false;
      }

      const [hour, minute] = this.formData.time!.split(':');

      const startDateUTC = DateTime.fromISO(this.formData.dateStart!).set({ hour: +hour, minute: +minute, second: 30, millisecond: 0 }).toUTC();
      const endDateUTC = this.formData.dateEnd ? DateTime.fromISO(this.formData.dateEnd!).set({ hour: +hour, minute: +minute, second: 30, millisecond: 0 }).toUTC() : null;

      const payload: Partial<Options> = {
        dtstart: startDateUTC.toJSDate(),
        freq: this.formData.frequency,
        byhour: [startDateUTC.hour],
        byminute: [startDateUTC.minute],
        bysecond: [30]
      };

      if (endDateUTC) {
        payload.until = endDateUTC.toJSDate();
      }

      if (this.formData.frequency === RRule.WEEKLY || this.formData.frequency === RRule.MONTHLY) {
        payload.interval = this.formData.interval;
        payload.byweekday = this.formData.dayOfWeek;
      }

      if (this.formData.frequency === RRule.MONTHLY) {
        payload.bysetpos = [this.formData.bysetpos];
      }

      this.$emit('submit', { rrule: new RRule(payload), dateEnd: this.formData.dateEnd || null, time: this.formData.time! });

      return true;
    }
  }
})
</script>
